<template>
  <v-card class="mt-6 shadow">
    <v-card-title class="d-flex justify-space-between align-center">
      <div>
        <div class="overline primary--text mb-1">Winners</div>
        <div class="text-h6">Otso Panalo Winners</div>
      </div>

      <div class="d-flex flex-wrap justify-end" style="gap: 8px">
        <v-btn
          v-if="canApproveWinners && hasPendingApprovals"
          color="success"
          outlined
          :loading="approveAllLoading"
          :disabled="approveAllButtonDisabled"
          @click="approveAllDialog = true"
        >
          <v-icon left>mdi-check-all</v-icon>
          Approve All
        </v-btn>

        <v-btn
          color="primary"
          :loading="generateLoading"
          :disabled="generateButtonDisabled"
          @click="handleGenerate"
        >
          <v-icon left>mdi-account-multiple-check-outline</v-icon>
          Generate Winners
        </v-btn>
      </div>
    </v-card-title>

    <v-divider></v-divider>

    <v-card-text>
      <v-row class="mb-3" align="center">
        <v-col cols="12" md="6">
          <div class="text-body-2 grey--text text--darken-1">
            Generate the winners list once the Otso Panalo result has been
            approved.
          </div>
        </v-col>

        <v-col cols="12" md="6" class="text-md-right">
          <span class="grey--text text-body-2 mr-2">Result Status</span>
          <v-chip small :class="resultStatusClass">
            {{ resultStatusLabel }}
          </v-chip>
        </v-col>
      </v-row>

      <v-alert v-if="message" :type="messageType" dense outlined class="mb-4">
        {{ message }}
      </v-alert>

      <v-data-table
        :headers="headers"
        :items="winners"
        :loading="tableLoading"
        :items-per-page="5"
        loading-text="Loading... Please wait"
        :no-data-text="noDataText"
        disable-sort
      >
        <template v-slot:[`item.action`]="{ item }">
          <div class="text-center">
            <v-tooltip
              top
              v-if="item.status !== 'Approved' && canApproveWinners"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="success"
                  v-bind="attrs"
                  v-on="on"
                  :disabled="approvalInProgress"
                  @click="confirmApprove(item)"
                >
                  <v-icon>mdi-checkbox-marked-circle-outline</v-icon>
                </v-btn>
              </template>
              <span>Approve winner</span>
            </v-tooltip>

            <v-icon v-else-if="item.status === 'Approved'" class="green--text">
              mdi-checkbox-marked-circle-outline
            </v-icon>

            <span v-else class="grey--text">-</span>
          </div>
        </template>

        <template v-slot:[`item.prize`]="{ item }">
          {{ moneyFormat(item.prize) }}
        </template>

        <template v-slot:[`item.winning_quarters`]="{ item }">
          <div class="d-flex flex-column align-start py-2" style="gap: 4px">
            <v-chip
              v-for="quarter in winningQuarterRows(item)"
              :key="quarter.label"
              :class="quarterStatusClass(quarter.status)"
            >
              {{ quarterChipLabel(quarter) }}
            </v-chip>

            <span v-if="!winningQuarterRows(item).length" class="grey--text">
              -
            </span>
          </div>
        </template>

        <template v-slot:[`item.status`]="{ item }">
          <v-chip
            small
            :class="
              item.status === 'Approved'
                ? 'green lighten-5 green--text'
                : 'orange lighten-5 orange--text'
            "
          >
            {{ item.status || "Pending" }}
          </v-chip>
        </template>
      </v-data-table>
    </v-card-text>

    <v-dialog v-model="approveDialog" max-width="320">
      <v-card class="py-2">
        <v-card-title class="text-h6 d-block text-center">
          Confirm Approval
        </v-card-title>

        <v-card-text class="text-center">
          Approve this winner record?
        </v-card-text>

        <v-card-actions class="d-block text-center">
          <v-btn
            color="success"
            outlined
            small
            :loading="approveLoading"
            @click="handleApproveSingle"
          >
            Yes
          </v-btn>

          <v-btn color="grey" outlined small @click="approveDialog = false">
            No
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="approveAllDialog" max-width="320">
      <v-card class="py-2">
        <v-card-title class="text-h6 d-block text-center">
          Confirm Approve All
        </v-card-title>

        <v-card-text class="text-center">
          Approve all pending winners for this game?
        </v-card-text>

        <v-card-actions class="d-block text-center">
          <v-btn
            color="success"
            outlined
            small
            :loading="approveAllLoading"
            @click="handleApproveAll"
          >
            Yes
          </v-btn>

          <v-btn color="grey" outlined small @click="approveAllDialog = false">
            No
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { mapState } from "vuex";
import responseGet from "@/helpers/api_request_get";
import responsePost from "@/helpers/api_request_post";

export default {
  props: {
    gameId: {
      type: [String, Number],
      required: true,
    },
    refreshKey: {
      type: Number,
      default: 0,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  data: () => ({
    generateLoading: false,
    approveLoading: false,
    approveAllLoading: false,
    loadingResultStatus: false,
    loadingWinners: false,
    generationCompleted: false,
    resultStatus: "",
    message: "",
    messageType: "info",
    winners: [],
    approveDialog: false,
    approveAllDialog: false,
    selectedWinnerId: null,
    headers: [
      {
        text: "Action",
        value: "action",
        class: "text-uppercase text-center",
        align: "center",
      },
      {
        text: "Winner ID",
        value: "id",
        class: "text-uppercase",
      },
      {
        text: "Bet ID",
        value: "bet_id",
        class: "text-uppercase",
      },
      {
        text: "Player Name",
        value: "player_name",
        class: "text-uppercase",
      },
      {
        text: "Prize Type",
        value: "winner_type",
        class: "text-uppercase",
      },
      {
        text: "Matched Quarters",
        value: "matched_quarters",
        class: "text-uppercase",
      },
      {
        text: "Winning Quarters",
        value: "winning_quarters",
        class: "text-uppercase",
      },
      {
        text: "Mobile Number",
        value: "mobile_number",
        class: "text-uppercase",
      },
      {
        text: "Prize",
        value: "prize",
        class: "text-uppercase",
      },
      {
        text: "Status",
        value: "status",
        class: "text-uppercase",
      },
    ],
  }),

  computed: {
    ...mapState(["accountInfo"]),

    resultStatusLabel() {
      return this.resultStatus || "No Result Yet";
    },

    canGenerateWinners() {
      return !!(
        this.accountInfo.access && this.accountInfo.access.includes(107)
      );
    },

    canApproveWinners() {
      return !!(
        this.accountInfo.access && this.accountInfo.access.includes(109)
      );
    },

    hasPendingApprovals() {
      return this.winners.some((winner) => winner.status !== "Approved");
    },

    approvalInProgress() {
      return this.approveLoading || this.approveAllLoading;
    },

    resultStatusClass() {
      if (this.resultStatus === "Approved") {
        return "green lighten-5 green--text";
      }

      if (this.resultStatus === "For Approval") {
        return "orange lighten-5 orange--text";
      }

      return "blue-grey lighten-5 blue-grey--text text--darken-1";
    },

    tableLoading() {
      return this.loadingResultStatus || this.loadingWinners;
    },

    noDataText() {
      if (this.resultStatus === "Approved" && this.generationCompleted) {
        return "Winner generation is complete. No players matched the approved result.";
      }

      return "No winners generated yet.";
    },

    generateButtonDisabled() {
      return (
        this.disabled ||
        this.loadingResultStatus ||
        this.loadingWinners ||
        this.generateLoading ||
        this.generationCompleted ||
        !this.canGenerateWinners ||
        this.resultStatus !== "Approved"
      );
    },

    approveAllButtonDisabled() {
      return (
        this.disabled ||
        this.loadingWinners ||
        this.loadingResultStatus ||
        this.generateLoading ||
        this.approvalInProgress ||
        !this.canApproveWinners ||
        !this.hasPendingApprovals
      );
    },
  },

  created() {
    this.refreshData();
  },

  watch: {
    gameId() {
      this.refreshData();
    },

    refreshKey() {
      this.refreshData();
    },
  },

  methods: {
    async refreshData() {
      if (!this.gameId) {
        this.generationCompleted = false;
        this.resultStatus = "";
        this.winners = [];
        this.message = "";
        return;
      }

      this.generationCompleted = false;

      const statusLoaded = await this.loadResultStatus();
      const winnersLoaded = await this.loadWinners();

      if (this.winners.length) {
        this.generationCompleted = true;
      }

      if (statusLoaded && winnersLoaded) {
        this.setDefaultMessage();
      }
    },

    async loadResultStatus() {
      if (!this.gameId) {
        this.resultStatus = "";
        return false;
      }

      try {
        this.loadingResultStatus = true;

        const response = await responseGet(
          "",
          `/otso_panalo/results/details/${this.gameId}`,
        );

        if (response.status === 200) {
          const details = response.data.details || null;
          this.resultStatus = details ? details.status || "" : "";
          return true;
        }

        if (response.status === 401) {
          this.$store.commit("setError", "Unauthorized");
        } else {
          this.resultStatus = "";
          this.messageType = "error";
          this.message = "Failed to load Otso Panalo result status.";
        }
      } catch (error) {
        this.resultStatus = "";
        this.messageType = "error";
        this.message = "Failed to load Otso Panalo result status.";
      } finally {
        this.loadingResultStatus = false;
      }

      return false;
    },

    async loadWinners() {
      if (!this.gameId) {
        this.winners = [];
        return false;
      }

      try {
        this.loadingWinners = true;

        const response = await responseGet(
          "",
          `/otso_panalo/winners/${this.gameId}`,
        );

        if (response.status === 200) {
          this.winners = response.data.records || [];
          this.generationCompleted = Boolean(response.data.generated);
          return true;
        }

        if (response.status === 401) {
          this.$store.commit("setError", "Unauthorized");
        } else {
          this.messageType = "error";
          this.message = "Failed to load Otso Panalo winners.";
        }
      } catch (error) {
        this.messageType = "error";
        this.message = "Failed to load Otso Panalo winners.";
      } finally {
        this.loadingWinners = false;
      }

      return false;
    },

    confirmApprove(item) {
      this.selectedWinnerId = item.id;
      this.approveDialog = true;
    },

    async handleApproveSingle() {
      if (!this.selectedWinnerId || this.approvalInProgress) {
        return;
      }

      try {
        this.approveLoading = true;

        const formData = new FormData();
        formData.append("game_id", this.gameId);
        formData.append("winner_id", this.selectedWinnerId);

        const response = await responsePost(
          formData,
          "/otso_panalo/winners/approve",
        );

        if (response.status === 200) {
          this.winners = response.data.records || [];
          this.approveDialog = false;
          this.selectedWinnerId = null;
          this.setDefaultMessage(
            response.data.message ||
              "Otso Panalo winner approved successfully.",
          );
        } else if (response.status === 401) {
          this.$store.commit("setError", "Unauthorized");
        } else {
          this.messageType = "error";
          this.message =
            response.data.error ||
            response.data.message ||
            "Failed to approve Otso Panalo winner.";
        }
      } catch (error) {
        this.messageType = "error";
        this.message = "Failed to approve Otso Panalo winner.";
      } finally {
        this.approveLoading = false;
      }
    },

    async handleApproveAll() {
      if (this.approveAllButtonDisabled) {
        return;
      }

      try {
        this.approveAllLoading = true;

        const formData = new FormData();
        formData.append("game_id", this.gameId);

        const response = await responsePost(
          formData,
          "/otso_panalo/winners/approve_all",
        );

        if (response.status === 200) {
          this.winners = response.data.records || [];
          this.approveAllDialog = false;
          this.setDefaultMessage(
            response.data.message ||
              "All Otso Panalo winners approved successfully.",
          );
        } else if (response.status === 401) {
          this.$store.commit("setError", "Unauthorized");
        } else {
          this.messageType = "error";
          this.message =
            response.data.error ||
            response.data.message ||
            "Failed to approve Otso Panalo winners.";
        }
      } catch (error) {
        this.messageType = "error";
        this.message = "Failed to approve Otso Panalo winners.";
      } finally {
        this.approveAllLoading = false;
      }
    },

    setDefaultMessage(customMessage = "") {
      if (customMessage) {
        this.messageType = this.winners.length ? "success" : "info";
        this.message = customMessage;
        return;
      }

      if (!this.resultStatus) {
        this.messageType = "info";
        this.message =
          "Create and approve a result first before generating winners.";
        return;
      }

      if (this.resultStatus !== "Approved") {
        this.messageType = "info";
        this.message =
          "Winner generation is disabled until the result status becomes Approved.";
        return;
      }

      if (this.winners.length) {
        this.messageType = "success";
        this.message = `${this.winners.length} Otso Panalo winner(s) generated.`;
        return;
      }

      if (this.generationCompleted) {
        this.messageType = "info";
        this.message =
          "Winner generation is complete. No players matched the approved result.";
        return;
      }

      this.messageType = "info";
      this.message = "The result is approved. You can generate winners now.";
    },

    async handleGenerate() {
      if (this.generateButtonDisabled || this.approvalInProgress) {
        return;
      }

      try {
        this.generateLoading = true;

        const formData = new FormData();
        formData.append("game_id", this.gameId);

        const response = await responsePost(
          formData,
          "/otso_panalo/winners/generate",
        );

        if (response.status === 200) {
          this.winners = response.data.records || [];
          this.generationCompleted = true;
          this.setDefaultMessage(response.data.message || "");
          this.$emit(
            "generated",
            response.data.message ||
              "Otso Panalo winners generated successfully.",
          );
        } else if (response.status === 401) {
          this.$store.commit("setError", "Unauthorized");
        } else {
          this.messageType = "error";
          this.message =
            response.data.message || "Failed to generate Otso Panalo winners.";
        }
      } catch (error) {
        this.messageType = "error";
        this.message = "Failed to generate Otso Panalo winners.";
      } finally {
        this.generateLoading = false;
      }
    },

    moneyFormat(value) {
      const amount = Number(value || 0);

      return `PHP ${amount.toLocaleString("en-PH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    },

    quarterRows(item) {
      return [
        {
          label: "Q1",
          result: item.first_quarter_result,
          status: item.first_quarter_status,
        },
        {
          label: "Q2",
          result: item.second_quarter_result,
          status: item.second_quarter_status,
        },
        {
          label: "Q3",
          result: item.third_quarter_result,
          status: item.third_quarter_status,
        },
        {
          label: "Q4",
          result: item.fourth_quarter_result,
          status: item.fourth_quarter_status,
        },
      ];
    },

    winningQuarterRows(item) {
      return this.quarterRows(item).filter(
        (quarter) => quarter.status === "Win",
      );
    },

    quarterChipLabel(quarter) {
      return quarter.result
        ? `${quarter.label}: ${quarter.result}`
        : quarter.label;
    },

    quarterStatusClass(status) {
      if (status === "Win") {
        return "green lighten-5 green--text";
      }

      if (status === "Lose") {
        return "red lighten-5 red--text";
      }

      return "grey lighten-3 grey--text text--darken-1";
    },
  },
};
</script>
